import {
  Group,
  Text,
  ActionIcon,
  Stack,
  Paper,
  LoadingOverlay,
  Badge,
  Divider,
} from '@mantine/core';
import { format } from 'date-fns';
import { CiCalendar } from 'react-icons/ci';
import { IoTimeOutline } from 'react-icons/io5';
import { FaRegTrashCan } from 'react-icons/fa6';
import { AvailabilityEntry } from '@worker/types/myJobs.type';
import { isMobile } from 'react-device-detect';
import { formatTime12h } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';

interface AvailabilityListProps {
  entries: AvailabilityEntry[];
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

interface AvailabilityRowProps {
  entry: AvailabilityEntry;
  onDelete: (id: string) => void;
}

function AvailabilityRow({ entry, onDelete }: AvailabilityRowProps) {
  const dateDisplay = (
    <Group gap="xs" wrap="nowrap">
      <CiCalendar size={18} color="var(--mantine-color-dimmed)" />
      <Text size="sm" data-testid={`availability-date-${entry.id}`}>
        {format(entry.startDate, DATE_FORMATS.DATE_FORMAT_SHORT)} –{' '}
        {format(entry.endDate, DATE_FORMATS.DATE_FORMAT_SHORT)}
      </Text>
    </Group>
  );

  const timeDisplay = entry.isAllDay ? (
    <Badge variant="light" color="blue" size="md">
      All Day
    </Badge>
  ) : (
    <Group gap="xs" wrap="nowrap">
      <IoTimeOutline size={16} color="var(--mantine-color-dimmed)" />
      <Text size="sm" c="dimmed">
        {formatTime12h(entry.startTime || '')} –{' '}
        {formatTime12h(entry.endTime || '')}
      </Text>
    </Group>
  );

  const deleteButton = (
    <ActionIcon
      variant="subtle"
      color="red"
      onClick={() => onDelete(entry.id)}
      aria-label="Delete availability"
      data-testid={`availability-delete-${entry.id}`}
    >
      <FaRegTrashCan size={16} />
    </ActionIcon>
  );

  if (isMobile) {
    return (
      <>
        <Group
          align="center"
          justify="space-between"
          // bd="1px solid var(--mantine-color-gray-3)"
          data-testid={`availability-entry-${entry.id}`}
        >
          <Stack gap={4}>
            {dateDisplay}
            {timeDisplay}
          </Stack>
          {deleteButton}
        </Group>
        <Divider />
      </>
    );
  }

  return (
    <Paper
      p="xs"
      withBorder
      shadow="xs"
      radius="md"
      data-testid={`availability-entry-${entry.id}`}
    >
      <Group gap="md" align="center" wrap="nowrap">
        {dateDisplay}
        {timeDisplay}
        <Group ml="auto">{deleteButton}</Group>
      </Group>
    </Paper>
  );
}

export default function AvailabilityList({
  entries,
  onDelete,
  isLoading = false,
}: AvailabilityListProps) {
  return (
    <Stack gap="sm" pos="relative" data-testid="availability-list">
      <LoadingOverlay visible={isLoading} />
      {entries.map((entry) => (
        <AvailabilityRow key={entry.id} entry={entry} onDelete={onDelete} />
      ))}
    </Stack>
  );
}
